import React, {FC} from 'react';
import styled from 'styled-components';
import {
  HelpCenterLandingPageData,
  HelpCenterPageCommonProps,
} from '../../../pages/HelpCenter/types';
import {complementaryColor} from '../../constants/colors';
import {
  mediaMinDesktopLarge,
  mediaMinTablet,
} from '../../constants/customMedia';
import {
  fsS,
  fsXL,
  fsXS,
  fsXXL,
  fsXXS,
  fsXXXL,
} from '../../constants/typography';

import {HelpCenterEndpoint} from '../../../pages/HelpCenter/helpCenterEndpoint';
import {gridGapPx} from '../../constants/misc';
import PageWrapper from '../common/PageWrapper';
import Suggestions from '../common/Suggestions';

const Header = styled.div<{
  headerImageLarge: HelpCenterLandingPageData['headerImageLarge'];
  headerImageSmall: HelpCenterLandingPageData['headerImageSmall'];
}>`
  background-color: #999;
  height: 300px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px;
  background-image: url(${props => props.headerImageSmall});
  background-size: cover;
  background-position: right center;

  @media ${mediaMinTablet} {
    background-image: url(${props => props.headerImageLarge});
    height: 380px;
    padding: 40px 60px;
  }
  @media ${mediaMinDesktopLarge} {
    height: 440px;
  }
`;

const SubTitle = styled.h2`
  font-size: ${fsXXS}px;
  font-weight: bold;
  line-height: 1.5;
  text-align: center;
  color: ${complementaryColor};
  padding-bottom: 4px;
  margin: 0;

  @media ${mediaMinTablet} {
    font-size: ${fsXS}px;
    line-height: 1.71;
    padding-bottom: 8px;
  }
  @media ${mediaMinDesktopLarge} {
    font-size: ${fsS}px;
    padding-bottom: 10px;
    line-height: 1.5;
  }
`;
const Title = styled.h3`
  font-size: ${fsXL}px;
  font-weight: 900;
  line-height: 1.25;
  letter-spacing: 0.35px;
  text-align: center;
  color: ${complementaryColor};
  padding-bottom: 18px;
  margin: 0;
  flex-grow: 1;

  @media ${mediaMinTablet} {
    font-size: ${fsXXL}px;
    line-height: 1.1;
    letter-spacing: 0.4px;
    padding-bottom: 40px;
  }
  @media ${mediaMinDesktopLarge} {
    font-size: ${fsXXXL}px;
    line-height: 1.13;
    letter-spacing: 0.5px;
  }
`;

const HeaderContentWrapper = styled(PageWrapper)`
  background-color: transparent;
`;

const TextFieldWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  margin-bottom: ${gridGapPx};
  z-index: 1;
  width: 100%;
  flex-grow: 1;
  align-self: center;

  @media ${mediaMinTablet} {
    & > div {
      width: 536px;
    }
  }
`;

interface Props {
  title: string;
  subTitle: string;
  locale: HelpCenterPageCommonProps['locale'];
  sections: HelpCenterPageCommonProps['sections'];
  highlightQuestions: HelpCenterLandingPageData['highlightQuestions'];
  headerImageLarge: HelpCenterLandingPageData['headerImageLarge'];
  headerImageSmall: HelpCenterLandingPageData['headerImageSmall'];
  translations: HelpCenterPageCommonProps['translations'];
  endpoints: HelpCenterEndpoint;
  isMobileHeaderShown?: boolean;
  showLeftArrow?: boolean;
}

const HelpdeskLandingPageHeader: FC<Props> = ({
  title,
  subTitle,
  locale,
  headerImageLarge,
  headerImageSmall,
  translations,
  endpoints,
  sections,
  isMobileHeaderShown = true,
  showLeftArrow = false,
}) => {
  return (
    <Header
      headerImageLarge={headerImageLarge}
      headerImageSmall={headerImageSmall}
      data-testid="HelpCenterTeaserContainer"
    >
      <HeaderContentWrapper>
        <SubTitle data-testid="HelpCenterTeaserTitle">{subTitle}</SubTitle>
        <Title data-testid="HelpCenterTeaserHeadline">{title}</Title>
        <TextFieldWrapper>
          <Suggestions
            locale={locale}
            translations={translations}
            endpoints={endpoints}
            sections={sections}
            isMobileHeaderShown={isMobileHeaderShown}
            showLeftArrow={showLeftArrow}
          />
        </TextFieldWrapper>
      </HeaderContentWrapper>
    </Header>
  );
};

export default HelpdeskLandingPageHeader;
